<template>
  <div class="settings">
    <header>
      <h1>{{ t('settings.title') }}</h1>
      <p class="lead">{{ t('settings.lead') }}</p>
    </header>

    <div class="pane-tabs" role="tablist" :aria-label="t('settings.title')">
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'play' }"
        :aria-selected="pane === 'play'"
        @click="setPane('play')"
      >
        {{ t('settings.tabs.play') }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'look' }"
        :aria-selected="pane === 'look'"
        @click="setPane('look')"
      >
        {{ t('settings.tabs.look') }}
      </button>
      <button
        v-if="isDev"
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'dev' }"
        :aria-selected="pane === 'dev'"
        @click="setPane('dev')"
      >
        {{ t('settings.tabs.dev') }}
      </button>
    </div>

    <section v-show="pane === 'look'" class="panel">
      <h2>{{ t('settings.appearance') }}</h2>
      <p class="hint">{{ t('settings.themeHint') }}</p>

      <fieldset class="scheme-switch">
        <legend class="visually-hidden">{{ t('settings.theme') }}</legend>
        <button
          type="button"
          class="scheme-btn"
          :class="{ active: theme.colorScheme === 'light' }"
          :aria-pressed="theme.colorScheme === 'light'"
          @click="theme.setScheme('light')"
        >
          {{ t('settings.light') }}
        </button>
        <button
          type="button"
          class="scheme-btn"
          :class="{ active: theme.colorScheme === 'dark' }"
          :aria-pressed="theme.colorScheme === 'dark'"
          @click="theme.setScheme('dark')"
        >
          {{ t('settings.dark') }}
        </button>
      </fieldset>

      <h3>{{ t('settings.accent') }}</h3>
      <p class="hint">{{ t('settings.accentHint') }}</p>

      <fieldset class="swatches">
        <legend class="visually-hidden">{{ t('settings.accent') }}</legend>
        <button
          v-for="preset in ACCENT_PRESETS"
          :key="preset.id"
          type="button"
          class="swatch"
          :class="{ selected: theme.presetId === preset.id }"
          :style="{ background: preset.hex }"
          :aria-pressed="theme.presetId === preset.id"
          :aria-label="t(`settings.presets.${preset.id}`)"
          :title="t(`settings.presets.${preset.id}`)"
          @click="theme.setAccent(preset.hex)"
        />
        <label class="swatch custom" :title="t('settings.custom')">
          <input
            type="color"
            :value="theme.accentHex.toLowerCase()"
            :aria-label="t('settings.custom')"
            @input="onCustom"
          />
        </label>
      </fieldset>

      <p class="preview-row">
        <span class="preview-chip">{{ t('settings.preview') }}</span>
        <code>{{ theme.accentHex }}</code>
      </p>
    </section>

    <div v-show="pane === 'play'">
    <section class="panel">
      <h2>{{ t('pwa.title') }}</h2>
      <p class="hint">{{ t('pwa.lead') }}</p>
      <PwaInstallButton tone="settings" />
      <template v-if="showDesktopFullscreen">
        <h3>{{ t('settings.fullscreen') }}</h3>
        <p class="hint">{{ t('settings.fullscreenHint') }}</p>
        <DesktopFullscreenButton tone="settings" />
      </template>
      <h3>{{ t('settings.pushTitle') }}</h3>
      <p class="hint">{{ t('settings.pushHint') }}</p>
      <p v-if="pushPerm === 'unsupported'" class="hint">{{ t('settings.pushUnsupported') }}</p>
      <p v-else-if="pushPerm === 'denied'" class="hint">{{ t('settings.pushDenied') }}</p>
      <p v-else-if="pushPerm === 'granted' && !muted" class="hint">{{ t('settings.pushOn') }}</p>
      <p v-else-if="pushPerm === 'granted'" class="hint">{{ t('settings.pushMuted') }}</p>
      <button
        v-if="pushPerm === 'default' || (pushPerm === 'granted' && muted)"
        type="button"
        class="save-btn"
        @click="onEnablePush"
      >
        {{ t('settings.pushEnable') }}
      </button>
      <button
        v-else-if="pushPerm === 'granted'"
        type="button"
        class="save-btn"
        @click="onMutePush"
      >
        {{ t('settings.pushDisable') }}
      </button>
    </section>

    <section class="panel duel-panel">
      <h2>{{ t('settings.duel') }}</h2>
      <p class="hint">{{ t('settings.duelHint') }}</p>
      <h3>{{ t('settings.cardArt') }}</h3>
      <p class="hint">{{ t('settings.cardArtHint') }}</p>
      <fieldset class="scheme-switch">
        <legend class="visually-hidden">{{ t('settings.cardArt') }}</legend>
        <button
          type="button"
          class="scheme-btn"
          :class="{ active: duelPrefs.cardArtRegion === 'tcg' }"
          :aria-pressed="duelPrefs.cardArtRegion === 'tcg'"
          @click="duelPrefs.setCardArtRegion('tcg')"
        >
          {{ t('settings.cardArtTcg') }}
        </button>
        <button
          type="button"
          class="scheme-btn"
          :class="{ active: duelPrefs.cardArtRegion === 'ocg' }"
          :aria-pressed="duelPrefs.cardArtRegion === 'ocg'"
          @click="duelPrefs.setCardArtRegion('ocg')"
        >
          {{ t('settings.cardArtOcg') }}
        </button>
      </fieldset>
      <DuelViewerPrefs tone="menu" :show-zoom="menusCompact" />
    </section>

    <section class="panel duel-panel">
      <h2>{{ t('settings.audio') }}</h2>
      <p class="hint">{{ t('settings.audioHint') }}</p>
      <MusicVolumeSlider tone="menu" />
      <MusicVolumeSlider tone="menu" channel="sfx" />
    </section>

    <section class="panel duel-panel">
      <h2>{{ t('settings.vsAi') }}</h2>
      <p class="hint">{{ t('settings.vsAiHint') }}</p>

      <h3>{{ t('settings.vsAiDifficulty') }}</h3>
      <p class="hint">{{ t('settings.vsAiDifficultyHint') }}</p>
      <fieldset class="scheme-switch wrap">
        <legend class="visually-hidden">{{ t('settings.vsAiDifficulty') }}</legend>
        <button
          v-for="level in AI_DECK_DIFFICULTIES"
          :key="level"
          type="button"
          class="scheme-btn"
          :class="{ active: duelPrefs.vsAiDifficulty === level }"
          :aria-pressed="duelPrefs.vsAiDifficulty === level"
          @click="duelPrefs.setVsAiDifficulty(level)"
        >
          {{ t(`settings.vsAiLevels.${level}`) }}
        </button>
      </fieldset>

      <h3>{{ t('settings.rankedPrompts') }}</h3>
      <p class="hint">{{ t('settings.rankedPromptsHint') }}</p>
      <label class="check-row">
        <input
          type="checkbox"
          :checked="duelPrefs.rankedPrompts"
          @change="onRankedPrompts"
        >
        <span>{{ t('settings.rankedPromptsOn') }}</span>
      </label>
    </section>

    <section id="feedback" class="panel duel-panel">
      <h2>{{ t('feedback.title') }}</h2>
      <p class="hint">{{ t('feedback.lead') }}</p>
      <FeedbackPanel />
    </section>
    </div>

    <section v-show="isDev && pane === 'dev'" class="panel duel-panel">
      <h2>{{ t('settings.devTools') }}</h2>
      <p class="hint">{{ t('settings.devToolsHint') }}</p>

      <template v-if="!devToolsUnlocked">
        <form class="dev-form" @submit.prevent="unlockDevTools">
          <label class="visually-hidden" for="devtools-password">
            {{ t('settings.devToolsPassword') }}
          </label>
          <PasswordInput
            id="devtools-password"
            v-model="devToolsPassword"
            variant="settings"
            autocomplete="off"
            :placeholder="t('settings.devToolsPassword')"
            @input="devToolsWrong = false"
          />
          <button type="submit" class="save-btn">
            {{ t('settings.devToolsUnlock') }}
          </button>
        </form>
        <p v-if="devToolsWrong" class="form-error">
          {{ t('settings.devToolsWrong') }}
        </p>
      </template>

      <template v-else>
        <p class="hint grant-hint">{{ t('settings.devToolsGrantHint') }}</p>
        <div class="dev-actions">
          <button type="button" class="save-btn" @click="grantAllCards">
            {{ t('settings.devToolsGrant') }}
          </button>
          <button
            type="button"
            class="scheme-btn lock-btn"
            @click="lockDevTools"
          >
            {{ t('settings.devToolsLock') }}
          </button>
        </div>
        <p class="hint grant-hint pack-hint">
          {{ t('settings.devToolsPacksHint') }}
        </p>
        <div class="dev-actions">
          <button
            type="button"
            class="save-btn"
            :disabled="claimingPacks"
            @click="claimShopPacks(5)"
          >
            {{ t('settings.devToolsPacksX5') }}
          </button>
          <button
            type="button"
            class="save-btn"
            :disabled="claimingPacks"
            @click="claimShopPacks(10)"
          >
            {{ t('settings.devToolsPacksX10') }}
          </button>
        </div>

        <h3>{{ t('settings.vsAiDecide') }}</h3>
        <fieldset class="scheme-switch">
          <legend class="visually-hidden">{{ t('settings.vsAiDecide') }}</legend>
          <button
            type="button"
            class="scheme-btn"
            :class="{ active: duelPrefs.vsAiDice }"
            :aria-pressed="duelPrefs.vsAiDice"
            @click="duelPrefs.setVsAiDice(true)"
          >
            {{ t('settings.vsAiDiceOn') }}
          </button>
          <button
            type="button"
            class="scheme-btn"
            :class="{ active: !duelPrefs.vsAiDice }"
            :aria-pressed="!duelPrefs.vsAiDice"
            @click="duelPrefs.setVsAiDice(false)"
          >
            {{ t('settings.vsAiDiceOff') }}
          </button>
        </fieldset>
        <template v-if="!duelPrefs.vsAiDice">
          <h3>{{ t('settings.vsAiFirst') }}</h3>
          <fieldset class="scheme-switch">
            <legend class="visually-hidden">{{ t('settings.vsAiFirst') }}</legend>
            <button
              type="button"
              class="scheme-btn"
              :class="{ active: duelPrefs.vsAiFirst === 'you' }"
              :aria-pressed="duelPrefs.vsAiFirst === 'you'"
              @click="duelPrefs.setVsAiFirst('you')"
            >
              {{ t('settings.vsAiYou') }}
            </button>
            <button
              type="button"
              class="scheme-btn"
              :class="{ active: duelPrefs.vsAiFirst === 'cpu' }"
              :aria-pressed="duelPrefs.vsAiFirst === 'cpu'"
              @click="duelPrefs.setVsAiFirst('cpu')"
            >
              {{ t('settings.vsAiCpu') }}
            </button>
          </fieldset>
        </template>

        <p class="hint grant-hint">{{ t('settings.devToolsResetHint') }}</p>
        <div class="dev-actions">
          <button type="button" class="save-btn" @click="resetAccount">
            {{ t('settings.devToolsReset') }}
          </button>
          <button type="button" class="save-btn" @click="addDevCoins">
            {{ t('settings.devToolsCoins') }}
          </button>
        </div>
        <p class="hint grant-hint">{{ t('settings.devToolsRankHint') }}</p>
        <div class="dev-actions">
          <select
            v-model="devLeagueKey"
            class="text-input"
            :aria-label="t('settings.devToolsRank')"
          >
            <option
              v-for="step in LEAGUE_STEPS"
              :key="leagueKey(step)"
              :value="leagueKey(step)"
            >
              {{ leagueLabel(step) }}
            </option>
          </select>
          <button type="button" class="save-btn" @click="applyDevLeague">
            {{ t('settings.devToolsRank') }}
          </button>
        </div>
        <p v-if="devToolsFlash" :class="devToolsFlashOk ? 'form-ok' : 'form-error'">
          {{ devToolsFlash }}
        </p>
        <p class="hint grant-hint">Barajas del evento random_deck, para afinar la mezcla.</p>
        <div class="dev-actions">
          <router-link to="/random-deck-preview" class="save-btn">
            Preview baraja aleatoria
          </router-link>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ACCENT_PRESETS } from '../theme/accent'
import MusicVolumeSlider from '../components/MusicVolumeSlider.vue'
import DuelViewerPrefs from '../components/DuelViewerPrefs.vue'
import PwaInstallButton from '../components/PwaInstallButton.vue'
import DesktopFullscreenButton from '../components/DesktopFullscreenButton.vue'
import FeedbackPanel from '../components/FeedbackPanel.vue'
import PasswordInput from '../components/PasswordInput.vue'
import { useThemeStore } from '../stores/theme'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useCollectionStore } from '../stores/collection'
import { useI18n } from '../i18n/t'
import { useCompactLayout } from '../composables/useCompactLayout'
import { useDesktopFullscreen } from '../composables/useDesktopFullscreen'
import { AI_DECK_DIFFICULTIES, LEAGUE_STEPS, type LeagueId } from '@shared/index'
import { resetAccountRemote } from '../account/sync'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import { useWalletStore } from '../stores/wallet'
import { useAuthStore } from '../stores/auth'
import { api } from '../api/http'
import {
  armTournamentPush,
  enableTournamentPush,
  muteTournamentPush,
  pushMuted,
  pushSupported,
} from '../push/arm'

const DEVTOOLS_UNLOCK_KEY = 'ygo-devtools-unlocked'
const DEVTOOLS_SECRET_KEY = 'ygo-devtools-secret'

function devSecret(): string {
  return sessionStorage.getItem(DEVTOOLS_SECRET_KEY) ?? ''
}

type SettingsPane = 'play' | 'look' | 'dev'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { menusCompact } = useCompactLayout()
const { available: canDesktopFullscreen } = useDesktopFullscreen()
const showDesktopFullscreen = computed(
  () => canDesktopFullscreen.value && !menusCompact.value,
)
const theme = useThemeStore()
const duelPrefs = useDuelPrefsStore()
const collection = useCollectionStore()
const wallet = useWalletStore()
const isDev = import.meta.env.DEV
const devLeagueKey = ref(leagueKey(LEAGUE_STEPS[0]!))

const devToolsPassword = ref('')
const devToolsWrong = ref(false)
const devToolsUnlocked = ref(
  sessionStorage.getItem(DEVTOOLS_UNLOCK_KEY) === '1',
)
const claimingPacks = ref(false)
const devToolsFlash = ref('')
const devToolsFlashOk = ref(true)
let grantedTimer: ReturnType<typeof setTimeout> | null = null

type PushPerm = NotificationPermission | 'unsupported'

function readPushPerm(): PushPerm {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

const pushPerm = ref<PushPerm>(readPushPerm())
const muted = ref(pushMuted())
if (pushPerm.value === 'granted' && !muted.value) armTournamentPush()

function onEnablePush(): void {
  void enableTournamentPush().then((perm) => {
    pushPerm.value = perm
    muted.value = pushMuted()
  })
}

function onMutePush(): void {
  void muteTournamentPush().then(() => {
    muted.value = true
  })
}

function paneFromHash(hash: string): SettingsPane {
  if (hash === '#dev' && import.meta.env.DEV) return 'dev'
  if (hash === '#look') return 'look'
  return 'play'
}

const pane = ref<SettingsPane>(paneFromHash(route.hash))

function setPane(next: SettingsPane): void {
  pane.value = next
  const hash = next === 'play' ? '' : `#${next}`
  if (route.hash === hash) return
  void router.replace({ hash })
}

function applyHash(): void {
  pane.value = paneFromHash(route.hash)
  if (route.hash !== '#feedback') return
  void nextTick(() => {
    const message = document.getElementById('feedback-message')
    if (route.query.report === 'bug' && message instanceof HTMLTextAreaElement) {
      message.focus()
      return
    }
    document.getElementById('feedback')?.scrollIntoView({ behavior: 'smooth' })
  })
}

onMounted(applyHash)
watch(() => route.hash, applyHash)

function onCustom(event: Event): void {
  const input = event.target as HTMLInputElement
  theme.setAccent(input.value)
}

function onRankedPrompts(event: Event): void {
  const input = event.target as HTMLInputElement
  duelPrefs.setRankedPrompts(input.checked)
}

/**
 * Unlocks DevTools for this tab session if the password matches.
 */
function unlockDevTools(): void {
  const typed = devToolsPassword.value.trim()
  if (!typed) {
    devToolsWrong.value = true
    return
  }
  devToolsWrong.value = false
  devToolsPassword.value = ''
  devToolsUnlocked.value = true
  sessionStorage.setItem(DEVTOOLS_SECRET_KEY, typed)
  sessionStorage.setItem(DEVTOOLS_UNLOCK_KEY, '1')
}

function lockDevTools(): void {
  devToolsUnlocked.value = false
  devToolsFlash.value = ''
  sessionStorage.removeItem(DEVTOOLS_UNLOCK_KEY)
  sessionStorage.removeItem(DEVTOOLS_SECRET_KEY)
}

/**
 * Brief status line under DevTools actions.
 */
function flashDevTools(message: string, ok = true): void {
  devToolsFlash.value = message
  devToolsFlashOk.value = ok
  if (grantedTimer) clearTimeout(grantedTimer)
  grantedTimer = setTimeout(() => {
    devToolsFlash.value = ''
    grantedTimer = null
  }, 2400)
}

/**
 * Puts every catalog card in the collection with 3 copies of each rarity.
 */
async function grantAllCards(): Promise<void> {
  const ok = await collection.grantAllPrints(devSecret())
  if (ok) {
    flashDevTools(t('settings.devToolsGranted'))
  } else {
    flashDevTools(t('settings.devToolsFailed'), false)
  }
}

/**
 * Credits unopened shop boosters without spending coins.
 */
async function claimShopPacks(qty: number): Promise<void> {
  if (claimingPacks.value) return
  claimingPacks.value = true
  try {
    const ok = await collection.grantShopPacks(qty, devSecret())
    if (ok) {
      flashDevTools(t('settings.devToolsPacksGranted', { n: qty }))
    } else {
      flashDevTools(t('settings.devToolsFailed'), false)
    }
  } finally {
    claimingPacks.value = false
  }
}

function leagueKey(step: (typeof LEAGUE_STEPS)[number]): string {
  return step.division ? `${step.league}-${step.division}` : step.league
}

function leagueLabel(step: (typeof LEAGUE_STEPS)[number]): string {
  const name = t(`rank.leagues.${step.league}`)
  if (!step.division) return name
  return `${name} ${step.division === 1 ? 'I' : 'II'}`
}

function parseDevLeague(): (typeof LEAGUE_STEPS)[number] | null {
  return LEAGUE_STEPS.find((step) => leagueKey(step) === devLeagueKey.value)
    ?? null
}

/**
 * Wipe the signed-in account back to starters.
 */
async function resetAccount(): Promise<void> {
  const ok = await resetAccountRemote(devSecret())
  flashDevTools(
    ok ? t('settings.devToolsResetDone') : t('settings.devToolsFailed'),
    ok,
  )
}

/**
 * Credit 100 coins on the signed-in account.
 */
async function addDevCoins(): Promise<void> {
  const ok = await wallet.grantDevCoins(devSecret())
  flashDevTools(
    ok ? t('settings.devToolsCoinsDone') : t('settings.devToolsFailed'),
    ok,
  )
}

/**
 * Write the selected league and skip placement.
 */
async function applyDevLeague(): Promise<void> {
  const step = parseDevLeague()
  const auth = useAuthStore()
  if (!step || !auth.token) {
    flashDevTools(t('settings.devToolsFailed'), false)
    return
  }
  try {
    const res = await api<{ leaguePeak?: LeagueId | null }>('/rating/dev-set', {
      method: 'POST',
      token: auth.token,
      body: JSON.stringify({
        password: devSecret(),
        league: step.league,
        division: step.division,
      }),
    })
    useLeagueRewardsStore().apply(res.leaguePeak)
    flashDevTools(t('settings.devToolsRankDone'))
  } catch {
    flashDevTools(t('settings.devToolsFailed'), false)
  }
}

onUnmounted(() => {
  if (grantedTimer) clearTimeout(grantedTimer)
})
</script>

<style scoped>
.settings {
  width: min(960px, 100%);
  margin: 0 auto;
  padding: 12px 16px 48px;
  font-family: Roboto, system-ui, sans-serif;
  color: var(--md-sys-color-on-surface);
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.lead {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.pane-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 16px;
}

.pane-tab {
  min-height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.pane-tab.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.panel {
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.duel-panel {
  margin-top: 16px;
}

h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 400;
}

h3 {
  margin: 24px 0 8px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.15px;
}

.scheme-switch {
  display: flex;
  gap: 0;
  width: fit-content;
  margin: 0 0 8px;
  padding: 4px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 20px;
}

.scheme-switch.wrap {
  flex-wrap: wrap;
  max-width: 100%;
}

.scheme-switch.wrap .scheme-btn {
  padding: 0 12px;
}

#feedback {
  scroll-margin-top: 80px;
}

.scheme-btn {
  min-height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.scheme-btn.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
}

.hint {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0;
  padding: 0;
  border: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.swatch {
  width: 40px;
  height: 40px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  box-shadow: 0 0 0 1px var(--md-swatch-inset) inset;
}

.swatch.selected {
  box-shadow:
    0 0 0 2px var(--md-sys-color-surface, #131316),
    0 0 0 4px var(--md-sys-color-primary);
}

.swatch.custom {
  display: grid;
  place-items: center;
  background:
    conic-gradient(
      from 180deg,
      #e8c547,
      #ffb77c,
      #ff8a80,
      #ff80ab,
      #d0bcff,
      #8ab4f8,
      #4dd0e1,
      #86d994,
      #e8c547
    );
  overflow: hidden;
}

.swatch.custom input {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
}

.preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 0;
  font-size: 14px;
}

.preview-chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-weight: 500;
}

code {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.dev-form,
.dev-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.grant-hint {
  margin: 0 0 12px;
}

.pack-hint {
  margin-top: 16px;
}

.text-input {
  flex: 1 1 200px;
  min-width: 0;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
}

.text-input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
}

.save-btn:hover {
  filter: brightness(1.08);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: none;
}

.lock-btn {
  min-height: 40px;
  padding: 0 16px;
}

.form-error {
  margin: 12px 0 0;
  color: var(--md-sys-color-error, #ba1a1a);
  font-size: 14px;
  font-weight: 500;
}

.form-ok {
  margin: 12px 0 0;
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 720px) {
  .settings {
    padding: 20px 16px 40px;
  }

  h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .scheme-btn {
    min-height: 44px;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .settings {
    padding: 12px 16px 28px;
  }

  h1 {
    font-size: 22px;
    line-height: 28px;
  }

  .lead {
    margin-bottom: 12px;
  }

  .panel {
    padding: 14px;
  }
}
</style>
